import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import Modal from './ui/Modal';
import useCopyToClipboard from '../hooks/useCopyToClipboard';
import { containerVariants, itemVariants, iconVariants } from '../utils/animations';

function ShareModal({ puzzleId, expiresAt, onClose }) {
  const { copied, copyToClipboard } = useCopyToClipboard();

  const shareUrl = `${window.location.origin}/play/${puzzleId}`;

  const handleCopy = () => {
    copyToClipboard(shareUrl);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Check out my puzzle!',
          text: 'I made a puzzle for you to solve!',
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or share failed
      }
    }
  };

  const getTimeRemaining = () => {
    if (!expiresAt) return null;
    const now = new Date();
    const expires = new Date(expiresAt);
    const hoursRemaining = Math.round((expires - now) / (1000 * 60 * 60));
    return hoursRemaining;
  };

  const hoursRemaining = getTimeRemaining();
  const canNativeShare = !!navigator.share;

  return (
    <Modal onClose={onClose}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <motion.div className="flex items-center justify-center mb-6" variants={iconVariants}>
          <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </motion.div>

        <motion.h2
          className="text-xl font-bold text-puzzle-text text-center mb-2"
          variants={itemVariants}
        >
          Puzzle Created!
        </motion.h2>
        <motion.p
          className="text-puzzle-text-secondary text-center mb-6"
          variants={itemVariants}
        >
          Share this link with friends so they can solve your puzzle
        </motion.p>

        {/* Share URL */}
        <motion.div className="bg-puzzle-muted rounded-lg p-3 mb-4" variants={itemVariants}>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="flex-1 bg-transparent text-sm text-puzzle-text outline-none truncate"
            />
            <button
              type="button"
              onClick={handleCopy}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                copied
                  ? 'bg-green-100 text-green-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </motion.div>

        {/* Native Share Button (mobile) */}
        {canNativeShare && (
        <motion.button
          type="button"
          onClick={handleNativeShare}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-puzzle-border rounded-lg text-puzzle-text hover:bg-puzzle-hover mb-4"
          variants={itemVariants}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share via...
        </motion.button>
        )}

        {/* Expiration Notice */}
        {hoursRemaining && (
        <motion.div
          className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2"
          variants={itemVariants}
        >
          <svg className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm">
            <span className="font-medium text-amber-700">Guest puzzle</span>
            <p className="text-amber-600">
              {`This puzzle will expire in ${hoursRemaining} hours. `}
              Sign up to keep your puzzles forever!
            </p>
          </div>
        </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div className="flex gap-3" variants={itemVariants}>
          <motion.button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Play Now
          </motion.button>
        </motion.div>
      </motion.div>
    </Modal>
  );
}

ShareModal.propTypes = {
  puzzleId: PropTypes.string.isRequired,
  expiresAt: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

ShareModal.defaultProps = {
  expiresAt: null,
};

export default ShareModal;
